import React, { FC, useEffect, useState } from "react";
import Button from "shared/ui/button";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import FormField from "shared/ui/form-field";
import { PasswordField } from "../components";
import { LoginUserDto, ResponseCodes, sircapApi } from "shared/api/sircap";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { PATH_LIST } from "shared/lib/react-router";
import { toast } from "react-toastify";
import { useStore } from "zustand";
import { onboardModel } from "entities/onboard";
import { RegisterEnum } from "entities/onboard/model/onboardModel";
import { useSchema } from "helpers/functions";
import { sessionModel } from "entities/session";
import { accountStore } from "entities/account";
import IndividualBusinessToggle from "widgets/account-type";

const validateEmail = (email: string | undefined) => {
  return Yup.string().email().isValidSync(email);
};

const validatePhone = (phone: string | undefined) => {
  return Yup.string()
    .test((phone) => {
      return phone &&
        phone.toString().length >= 8 &&
        phone.toString().length <= 14
        ? true
        : false;
    })
    .isValidSync(phone);
};

const LoginSchema = (t: any) =>
  Yup.object().shape({
    email_or_phone: Yup.string()
      .required(t("emailPhoneIsRequired"))
      .test("email_or_phone", "Email / Phone is invalid", (value) => {
        return validateEmail(value) || validatePhone(value);
      }),
    password: Yup.string().required(t("passwordIsRequired")),
  });

interface ILoginSchema {
  email_or_phone: string;
  password: string;
  remember_me: boolean;
}

type PropsType = {};

export const PasswordLoginForm: FC<PropsType> = ({}) => {
  const navigate = useNavigate();
  const { t: err } = useTranslation("errors");
  const { t } = useTranslation("onboarding-auth");

  const { syncUser, clear } = useStore(onboardModel.onboardStore);
  const { addUser } = useStore(sessionModel.sessionStore);
  const { synckyc, synckyb } = useStore(accountStore);
  const [isSubmit, setIsSubmit] = useState(false);
  const [isEntity, setIsEntity] = useState(false);
  const schema = useSchema(LoginSchema);
  const [initialEmail, setInitialEmail] = useState("");

  useEffect(() => {
    const login = localStorage.getItem("login");
    if (login) {
      setInitialEmail(login);
    }
  }, []);

  const onSubmit = async (values: ILoginSchema) => {
    setIsSubmit(true);
    const { email_or_phone, password, remember_me } = values;

    const form: LoginUserDto = {
      email: validateEmail(email_or_phone) ? email_or_phone : "",
      phone: validatePhone(email_or_phone)
        ? email_or_phone.replace(/\D/g, "")
        : "",
      password: password,
      entity: isEntity ? RegisterEnum.Business : RegisterEnum.Individual,
    };

    const resp = await sircapApi.auth.login(form);
    if (resp.error) {
      switch (resp.error.code) {
        case ResponseCodes.UserIsNotExists:
        case ResponseCodes.WrongEmailOrPassword:
          toast.error(err(String(resp.error.code)));
          break;

        default:
          break;
      }
    } else {
      clear();
      const {
        user,
        accessToken,
        refreshToken,
        entity,
        kyc,
        kyb,
        questionnaireStatus,
        biometrics,
        biometric,
        fcmToken,
        crsData,
        subAccounts,
      } = resp.data.data;
      syncUser(user, entity);
      synckyc(kyc || null);
      synckyb(kyb || null);
      accountStore.setState({ questionnaireStatus });
      addUser(
        user,
        accessToken,
        refreshToken,
        entity,
        biometrics,
        biometric,
        fcmToken,
        crsData,
        subAccounts
      );

      if (user.status !== 1) {
        navigate(PATH_LIST.chooseAccType);
      } else {
        if (remember_me) {
          localStorage.setItem("login", email_or_phone);
        } else {
          localStorage.removeItem("login");
        }
        // toast.success("Success login");
        const redirect = sessionStorage.getItem("redirect");
        if (redirect) {
          sessionStorage.removeItem("redirect");
          navigate(redirect);
        } else {
          navigate(PATH_LIST.home);
        }
      }
    }
    setIsSubmit(false);
  };

  return (
    <Formik
      enableReinitialize
      initialValues={{
        email_or_phone: initialEmail,
        password: "",
        remember_me: true, // initialEmail ? true : false,
      }}
      validationSchema={schema}
      onSubmit={(values) => {
        onSubmit({
          email_or_phone: values.email_or_phone.trim(),
          password: values.password.trim(),
          remember_me: values.remember_me,
        });
      }}
    >
      {(formik) => {
        const { errors, touched, isValid, dirty } = formik;
        return (
          <Form autoComplete="on">
            <IndividualBusinessToggle
              onIndividual={() => {
                setIsEntity(false);
              }}
              onBusiness={() => {
                setIsEntity(true);
              }}
            />

            <FormField
              type="text"
              id="email_or_phone"
              label={t("emailAddressOrPhoneNumber")}
              isInvalid={!!(errors.email_or_phone && touched.email_or_phone)}
              placeholder={t("enterYourEmailOrPhoneNumber")}
              autoComplete={"username"}
              autoFocus={false}
            />

            <PasswordField
              id="password"
              label={t("password")}
              isInvalid={!!(errors.password && touched.password)}
              placeholder={t("enterYourPassword")}
              autoComplete={"current-password"}
              autoFocus={false}
            />

            <Button
              type="submit"
              disabled={!isValid || isSubmit}
              variant="dark"
            >
              {t("logIn")}
            </Button>
          </Form>
        );
      }}
    </Formik>
  );
};
